import { Absence } from "@farmact/model/src/model/Absence";
import { AlertNotification } from "@farmact/model/src/model/AlertNotification";
import { AppCompany } from "@farmact/model/src/model/AppCompany";
import { AppUser } from "@farmact/model/src/model/AppUser";
import { Bill } from "@farmact/model/src/model/Bill";
import { BillReminder } from "@farmact/model/src/model/BillReminder";
import { BillChangeHistoryEntry } from "@farmact/model/src/model/changeHistory/BillChangeHistoryEntry";
import { ResourceInventoryHistoryEntry } from "@farmact/model/src/model/changeHistory/ResourceInventoryHistoryEntry";
import { CompanyMeta } from "@farmact/model/src/model/CompanyMeta";
import { AreaMeasurement } from "@farmact/model/src/model/AreaMeasurement";
import { Customer } from "@farmact/model/src/model/Customer";
import { DeliveryNote } from "@farmact/model/src/model/DeliveryNote";
import { Employee } from "@farmact/model/src/model/Employee";
import { EmployeeLocation } from "@farmact/model/src/model/EmployeeLocation";
import { Field } from "@farmact/model/src/model/Field";
import { GasStation } from "@farmact/model/src/model/GasStation";
import { GeolocationTracking } from "@farmact/model/src/model/GeolocationTracking";
import { Invitation } from "@farmact/model/src/model/Invitation";
import { LoadingOrUnloadingPoint } from "@farmact/model/src/model/LoadingOrUnloadingPoint";
import { Machine } from "@farmact/model/src/model/Machine";
import { MachineMaintenanceRecord } from "@farmact/model/src/model/MachineMaintenanceRecord";
import { MachineCounterTracking } from "@farmact/model/src/model/MachineCounterTracking";
import { Marker } from "@farmact/model/src/model/Marker";
import { MonthlyBillingData } from "@farmact/model/src/model/adminTools/MonthlyBillingData";
import { Offer } from "@farmact/model/src/model/Offer";
import { Order } from "@farmact/model/src/model/Order";
import { Project } from "@farmact/model/src/model/Project";
import { Refuel } from "@farmact/model/src/model/Refuel";
import { RentalOrder } from "@farmact/model/src/model/RentalOrder";
import { Resource } from "@farmact/model/src/model/Resource";
import { Service } from "@farmact/model/src/model/services/Service";
import { TimeTracking } from "@farmact/model/src/model/TimeTracking";
import { Track } from "@farmact/model/src/model/Track";
import { UpdateInfo } from "@farmact/model/src/model/UpdateInfo";
import { CreditNote } from "@farmact/model/src/model/CreditNote";
import { getModelConverter, PartialConstructor } from "./ModelConverter";
import { OperatingUnit } from "@farmact/model/src/model/OperatingUnit";
import { collection, CollectionReference, doc, Firestore } from "firebase/firestore";
import { FieldMeta } from "@farmact/model/src/model/FieldMeta";
import { TrackMeta } from "@farmact/model/src/model/TrackMeta";
import { UserMeta } from "@farmact/model/src/model/UserMeta";
import { MailTrace } from "@farmact/model/src/model/MailTrace";
import { Overtime } from "@farmact/model/src/model/Overtime";
import { LiquidMixture } from "@farmact/model/src/model/LiquidMixture";
import { SharingToken } from "@farmact/model/src/model/SharingToken";
import { DatevConnection } from "@farmact/model/src/model/DatevConnection";
import { TemplateData } from "@farmact/model/src/model/TemplateData";

export class FirestoreCollections {
    constructor(public firestore: Firestore) {}

    public appCompanies() {
        return this.useConverter(collection(this.firestore, "companies"), AppCompany);
    }

    public operatingUnits(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "operatingUnits"), OperatingUnit);
    }

    public datevConnections(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "datevConnections"), DatevConnection);
    }

    public roles() {
        return collection(this.firestore, "roles");
    }

    public invitations() {
        return this.useConverter(collection(this.firestore, "invitations"), Invitation);
    }

    public appUsers() {
        return this.useConverter(collection(this.firestore, "users"), AppUser);
    }

    public employees(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "employees"), Employee);
    }

    public fields(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "fields"), Field);
    }

    public fieldsMeta(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "fieldsMeta"), FieldMeta);
    }

    public loadingOrUnloadingPoints(companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.appCompanies(), companyId), "loadingPoints"),
            LoadingOrUnloadingPoint
        );
    }

    public markers(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "markers"), Marker);
    }

    public tracks(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "tracks"), Track);
    }

    public tracksMeta(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "tracksMeta"), TrackMeta);
    }

    public machines(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "machines"), Machine);
    }

    public machineCounterTrackings(companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.appCompanies(), companyId), "machineCounterTrackings"),
            MachineCounterTracking
        );
    }

    public machineMaintenanceRecords(machineId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.machines(companyId), machineId), "maintenanceRecords"),
            MachineMaintenanceRecord
        );
    }

    public gasStations(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "gasStations"), GasStation);
    }

    public refuels(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "refuels"), Refuel);
    }

    public customers(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "customers"), Customer);
    }

    public resources(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "resources"), Resource);
    }

    public resourceInventoryChangeHistory(resourceId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.resources(companyId), resourceId), "resourceInventoryChangeHistory"),
            ResourceInventoryHistoryEntry
        );
    }

    public orders(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "orders"), Order);
    }

    public geolocationTrackings(orderId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.orders(companyId), orderId), "geolocationTrackings"),
            GeolocationTracking
        );
    }

    public liquidMixtures(orderId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.orders(companyId), orderId), "liquidMixtures"), LiquidMixture);
    }

    public decoupledOrderTimeTrackings(orderId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.orders(companyId), orderId), "decoupledOrderTimeTrackings"),
            TimeTracking
        );
    }

    public employeeLocations(companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.appCompanies(), companyId), "metaInfos", "lastLocations", "locations"),
            EmployeeLocation
        );
    }

    public services(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "services"), Service);
    }

    public rentalOrders(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "rentalOrders"), RentalOrder);
    }

    public rentalOrderAreaMeasurements(rentalOrderId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(this.rentalOrders(companyId), rentalOrderId, "measurements"),
            AreaMeasurement
        );
    }

    public timeTrackings(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "timeTrackings"), TimeTracking);
    }

    public overtimes(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "overtimes"), Overtime);
    }

    public notifications(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "notifications"), AlertNotification);
    }

    public bills(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "bills"), Bill);
    }

    public billChangeHistory(billId: string, companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(doc(this.bills(companyId), billId), "billChangeHistory"),
            BillChangeHistoryEntry
        );
    }

    public billReminders(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "billReminders"), BillReminder);
    }

    public deliveryNotes(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "deliveryNotes"), DeliveryNote);
    }

    public creditNotes(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "creditNotes"), CreditNote);
    }

    public offers(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "offers"), Offer);
    }

    public sharingTokens(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "sharingTokens"), SharingToken);
    }

    public companyMails(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "mails"), MailTrace);
    }

    public absences(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "absences"), Absence);
    }

    public monthlyBillingData(companyId?: AppCompany["id"]) {
        return this.useConverter(
            collection(this.firestore, "billingData", companyId ?? "", "months"),
            MonthlyBillingData
        );
    }

    public companiesMeta() {
        return this.useConverter(collection(this.firestore, "companiesMeta"), CompanyMeta);
    }

    public usersMeta() {
        return this.useConverter(collection(this.firestore, "usersMeta"), UserMeta);
    }

    public templateData() {
        return this.useConverter(collection(this.firestore, "templateData"), TemplateData);
    }

    public serviceTemplates() {
        return this.useConverter(collection(this.firestore, "templateData/serviceTemplatesData/services"), Service);
    }

    public updateInfos() {
        return this.useConverter(collection(this.firestore, "updateInfos"), UpdateInfo);
    }

    public projects(companyId?: AppCompany["id"]) {
        return this.useConverter(collection(doc(this.appCompanies(), companyId), "projects"), Project);
    }

    private useConverter<T>(repo: CollectionReference, model: PartialConstructor<T>) {
        return repo.withConverter(getModelConverter(model));
    }
}
